import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import type { BuyRequest } from '@/js/services/RogueTypes'

// Five is a hardware ceiling, not a UI one: the upright mesh carries exactly five shelf sockets.
export const MAX_SHELVES = 5
const DEFAULT_VISIBLE = 2

// Shared by every custom product that reveals its "Shelf N" rows one at a time — Cave and Hybrid Rig — each keyed on its own bundle's metadata, so a side or unit counts its shelves independently.
// The bundle is taken as a ref/getter because add- and remove-unit flows replace the whole buy request tree; a captured object would leave the count writing to a discarded node.
export function useShelfVisibility(bundleBuyRequest: MaybeRefOrGetter<BuyRequest>, bundleOptionTitle: MaybeRefOrGetter<string>) {
  const visibleShelfCount = computed(() =>
    toValue(bundleBuyRequest)?.metadata?.visibleShelfCount ?? DEFAULT_VISIBLE
  )

  const shelfNumber = computed(() => {
    const match = toValue(bundleOptionTitle)?.match(/^Shelf (\d+)$/i)

    return match ? parseInt(match[1]) : null
  })

  const isShelf = computed(() => shelfNumber.value !== null)

  const isHiddenShelf = computed(() =>
    isShelf.value && shelfNumber.value! > visibleShelfCount.value
  )

  const isLastVisibleShelf = computed(() =>
    isShelf.value &&
    shelfNumber.value === visibleShelfCount.value &&
    visibleShelfCount.value < MAX_SHELVES
  )

  function addShelf() {
    const bundle = toValue(bundleBuyRequest)
    if (!bundle || visibleShelfCount.value >= MAX_SHELVES) return

    bundle.metadata = {
      ...bundle.metadata,
      visibleShelfCount: visibleShelfCount.value + 1,
    }
  }

  return {
    visibleShelfCount,
    shelfNumber,
    isShelf,
    isHiddenShelf,
    isLastVisibleShelf,
    addShelf,
  }
}
